import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import classNames from 'classnames';
import { components } from 'react-select';
import Creatable from 'react-select/creatable';
import css from './AutoComplete.css';
import ClearIcon from '../../resources/svg/clear_field.svg';
import TagInputSuggestion from '../widgets/TagInputSuggestion';
import SelectMenuList from '../widgets/SelectMenuList';
import SelectGroup from '../widgets/SelectGroup';
import Select from '../widgets/Select';
import AutoCompleteSingleValue from './AutoCompleteSingleValue';

const ClearIndicator = ({ innerProps: { ref, ...restInnerProps } }) => {
    return <ClearIcon className={css.clearIcon} {...restInnerProps} ref={ref} />;
};

const AutoComplete = ({
    defaultValue = '',
    onChange = () => {},
    onInputChange = () => {},
    placeholder = '',
    selectOptions = {},
    containerStyle = {},
    containerClass = {},
    isCreatable = false,
    creatableOptions = {},
    onBlur = () => {},
    controlledValue = null,
    searchable = false,
    error = '',
    big = false,
    hideDropdownIndicator = false,
    // For selects whose options load on open: without this the control looks inert precisely when
    // the user needs to click it, because there are no options to infer a dropdown from yet.
    alwaysShowDropdownIndicator = false,
    virtualScrolling = false,
    setFocus = false,
    dataTestId,
    ariaLabel = '',
    copiable = false,
}) => {
    const [selectedOption, setSelectedOption] = useState(defaultValue);
    const [, setSearchValue] = useState('');

    const selectRef = useRef(null);

    useEffect(() => {
        if (setFocus && selectRef.current?.inputRef) {
            selectRef.current.inputRef.focus();
        }
    }, [setFocus]);

    const handleChange = selectedOption => {
        let searchValue = null;
        if (selectedOption) {
            searchValue = typeof selectedOption === 'string' ? selectedOption : selectedOption.label;
        }
        setSelectedOption(selectedOption);
        setSearchValue(searchValue);
        if (typeof selectedOption === 'object') {
            onChange(selectedOption);
        }
    };

    const handleInputChange = (newVal, eventAction) => {
        const { action } = eventAction;
        const isSearchableOption = selectOptions?.isSearchable;
        if ((action !== 'input-change' && newVal === '') || !(searchable || isSearchableOption)) {
            return;
        }
        onInputChange(newVal);
    };

    const selectedValue = selectOptions?.disabled ? selectedOption || '' : selectedOption;
    const v = controlledValue !== null ? [controlledValue] : selectedValue;
    const subComponents = {
        SingleValue: AutoCompleteSingleValue,
        Option: TagInputSuggestion,
        IndicatorSeparator: selectOptions.isClearable ? components.IndicatorSeparator : null,
        ClearIndicator: selectOptions.isClearable ? ClearIndicator : null,
        DropdownIndicator:
            (alwaysShowDropdownIndicator || (selectOptions && selectOptions.options && selectOptions.options.length)) &&
            !hideDropdownIndicator
                ? components.DropdownIndicator
                : null,
    };

    if (virtualScrolling) {
        subComponents.MenuList = SelectMenuList;
        subComponents.Group = SelectGroup;
    }

    const baseProps = {
        value: v,
        onChange: handleChange,
        placeholder: <Translate id={placeholder} />,
        onCloseResetsInput: false,
        className: `${css.singularSelect} ${error ? css.error : ''} ${big ? css.big : ''}`,
        components: subComponents,
        isCopiable: copiable,
        isDisabled: selectOptions.disabled,
        isClearable: selectOptions.isClearable,
        isLoading: selectOptions.isLoading,
        loadingMessage: () => null,
        classNamePrefix: 'Select',
        onBlur,
        onFocus: () => {
            // this is used to select the input content on focus in case the field is searchable
            if (!selectOptions || !selectOptions.isSearchable) {
                return;
            }
            try {
                selectRef.current.inputRef.select();
            } catch (e) {}
        },
        ref: selectRef,
    };

    const creatableProps = {
        ...baseProps,
        ...selectOptions,
        ...creatableOptions,
        'aria-label': ariaLabel,
        onInputChange: handleInputChange,
        noResultsText: '',
        onBlurResetsInput: false,
        dataTestId,
    };

    const selectProps = {
        ...baseProps,
        ...selectOptions,
        'aria-label': ariaLabel,
        onInputChange: handleInputChange,
        noResultsText: '',
        onBlurResetsInput: false,
        dataTestId,
    };

    return (
        <div
            className={classNames(css.container, containerClass, { [css.disabled]: selectOptions.disabled })}
            style={containerStyle}
            data-testid={dataTestId}
        >
            {isCreatable ? <Creatable {...creatableProps} /> : <Select {...selectProps} />}
        </div>
    );
};

AutoComplete.propTypes = {
    defaultValue: PropTypes.any, // can be value or object with value
    onChange: PropTypes.func,
    onInputChange: PropTypes.func,
    placeholder: PropTypes.string,
    selectOptions: PropTypes.objectOf(PropTypes.any),
    containerStyle: PropTypes.objectOf(PropTypes.any),
    containerClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isCreatable: PropTypes.bool,
    creatableOptions: PropTypes.objectOf(PropTypes.any),
    onBlur: PropTypes.func,
    controlledValue: PropTypes.any,
    searchable: PropTypes.bool,
    copiable: PropTypes.bool,
    error: PropTypes.string,
    big: PropTypes.bool,
    hideDropdownIndicator: PropTypes.bool,
    alwaysShowDropdownIndicator: PropTypes.bool,
    virtualScrolling: PropTypes.bool,
    setFocus: PropTypes.bool,
    dataTestId: PropTypes.string,
    ariaLabel: PropTypes.string,
};

export default AutoComplete;
